// Selo institucional do Venda no Zap: símbolo branco (carrinho + calendário +
// check) sobre fundo laranja da marca (#F46A25), cantos arredondados.
//
// Único lockup pra TODOS os usos institucionais do produto — admin (fallback
// quando lojista não subiu logo), Login, ResetPassword, MagicLinkVerify,
// Checkout, e fallback do storefront/agenda quando a loja não tem logo.
//
// IMPORTANTE: NÃO substitui a logo PRÓPRIA das lojas. Só aparece como
// fallback quando store.logoUrl é vazio. Quando há logo da loja, ela é
// renderizada por cima (img normal) — esse componente é o "produto", não
// a marca do lojista.
//
// O SVG inline (paths brancos extraídos do favicon.svg, sem o rect de fundo)
// herda currentColor — fundo vem do bg-tailwind no container, evitando que
// um asset PNG no servidor (a versão verde legada em /api/public/logo.png)
// volte a aparecer.

import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg" | "xl";

interface BrandTileProps {
  size?: Size;
  className?: string;
  /** Override do label pra leitor de tela. Default: "Venda no Zap". */
  ariaLabel?: string;
}

const SIZE_MAP: Record<Size, { tile: string; svg: string; rounded: string }> = {
  sm: { tile: "w-8 h-8", svg: "w-5 h-5", rounded: "rounded-lg" },
  md: { tile: "w-12 h-12", svg: "w-8 h-8", rounded: "rounded-xl" },
  lg: { tile: "w-16 h-16", svg: "w-11 h-11", rounded: "rounded-2xl" },
  xl: { tile: "w-24 h-24", svg: "w-16 h-16", rounded: "rounded-2xl" },
};

export function BrandTile({ size = "md", className, ariaLabel = "Venda no Zap" }: BrandTileProps) {
  const s = SIZE_MAP[size];
  return (
    <div
      className={cn(
        "shrink-0 flex items-center justify-center text-white",
        s.tile,
        s.rounded,
        className,
      )}
      style={{ backgroundColor: "#F46A25" }}
      role="img"
      aria-label={ariaLabel}
    >
      <svg
        viewBox="0 0 512 512"
        className={s.svg}
        fill="currentColor"
        aria-hidden
      >
        <path d="M293.94,308.54l28.53-2.97,6.6,20.56-148.36,13.55c-6.61.6-12.64-.4-18.23,3.31-4.12,2.74-5.48,9.04-3.62,13.05,2.38,5.15,6.88,7.39,13.29,7.4l187.56.14c5.53,0,7.62,5.87,7.82,9.45.21,3.79-1.87,10.91-7.81,10.89l-194.78-.55c-20.05-.06-31.68-23.85-26.85-41.1,2.2-7.83,8.21-12.13,12.84-18.46l-61.5-165.33-44.93-7.34c-5.4-.88-8.12-5.85-8.16-10.22-.05-4.67,3.02-11.24,9.12-10.35l46.72,6.79c7.91,1.15,13.99,5.98,16.71,13.35l14.51,39.35,227.8-.04c9.47,0,18.13,6.02,18.33,15.66l-3.26,17.93c-12.15,5.47-21.53,13.74-30.35,24.45l-35.26.83-3.43,26.7,25.5.25c.44,4.21.34,8.45-1.19,12.26l-25.64.16c-1.67,6.9-1.89,13.04-1.97,20.28ZM183.53,236.79l-4.32-25.5h-47.73s9.07,25.65,9.07,25.65l42.99-.15ZM236.63,236.46l-.55-25.25h-44.54s4.47,25.76,4.47,25.76l40.61-.51ZM289.67,236.63l2.23-25.38-43.24-.09.22,25.81,40.79-.34ZM342.24,236.57l5.48-25.35-43.09-.02-2.42,25.52,40.02-.15ZM189.64,275.68l-4.12-26.97-40.16.27,10.35,27.07,33.93-.37ZM237.49,275.65l-.57-26.84-39.19-.24,4.51,27.25,35.25-.18ZM284.93,275.67l2.66-27.09-38.28.21.94,27.03,34.68-.15ZM196.72,316.82l-4.82-28.69-31.99.21,11.16,31.15,25.64-2.68ZM238.08,313.72l-.53-25.58-33.08.11,4.36,27.6,29.25-2.13ZM281.52,309.47l1.79-21.22-33.2-.07.26,24.26,31.15-2.96Z" />
        <path d="M392.83,101.2c13.73,1.89,24.95,12.41,25.01,26.49l.41,92.34c-5.68.44-9.94-1.06-16.3-2l-.49-59.46-217.85.02-.21,17.13-15.8-.13-.21-45.31c-.07-14.35,8.66-26.51,23.01-28.69l20.74-.7c.86,3.18,1.18,6.29.76,9.48-1.82,13.84,7.54,26.02,20.46,27.22,14,1.31,25.84-8.11,26.24-23.04l.38-14.03,68.03-.1.37,16.98c.27,12.24,11.76,20.62,23.19,20.32,11.34-.3,22.89-7.91,22.9-20.24l.02-16.65,19.35.36Z" />
        <path d="M208.76,418.46c2.79,14.5-4.77,28.5-17.98,31.63-15.35,3.63-29.49-5.17-33.38-19.34s4.95-27.99,18.93-31.91c13.46-3.77,29.47,4.22,32.43,19.62Z" />
        <path d="M315.1,447.78c-9.39-4.34-12.58-10.36-13.2-20-1.12-17.47,11.89-30.9,28.68-29.53,16.9,1.38,27.21,17.52,22.86,33.85-4.3,16.12-22.37,23.06-38.34,15.67Z" />
        <path d="M246.05,115.52c0,6.71-5.67,10.18-10.8,10.59-5.31.42-11.3-4.06-11.31-10.37l-.04-30.6c0-6.85,5.79-10.5,11.39-10.25,5.99.28,10.71,4.51,10.72,11.28l.04,29.34Z" />
        <path d="M361.5,114.05c.09,7.81-6.24,12.84-12.12,11.97-7.31-1.08-9.78-7.03-9.85-14.26l-.24-26.4c-.06-6.34,4.41-10.24,10.03-10.56s11.78,3.37,11.85,10.05l.33,29.19Z" />
        <path d="M403.27,352.12c-23.4,3.65-45.69-9.4-59.05-31.17-3.14-5.12-4.22-8.12-5.33-14.14-4.22-22.84,2.2-45.82,21.7-61.07,19.34-15.14,47.76-16.28,68.85-1.59,19.32,13.45,30.28,38.03,23.95,63.4-5.61,22.47-24.51,40.58-50.11,44.57ZM386.81,323.12c7.44-4.27,9.66-9.01,14.11-13.76l31.85-34.02c-1.91-4.84-5.98-8.88-11.2-9.59l-34.36,34.37-15.5-12.9c-3.82-3.18-14.63,6.09-10.95,9.78l26.05,26.12Z" />
      </svg>
    </div>
  );
}
